import classes from './OrganizationRowCell.module.css';
import * as React from 'react';
import { COLLAPSED_COLUMN_WIDTH, EXPANDED_COLUMN_WIDTH } from '../../layout';
import { useSolutionVersionsById, useSolutions } from '../../state/context';
import { Solution, SolutionVersion, Task } from '../../types';
import TaskList from '../TaskList';
import classNames from 'classnames';

type OrganizationRowCellProps = {
  tasks: Task[];
  isColumnExpanded: boolean;
};

type SolutionTasksMap = Record<number, {
  tasks: Task[],
  solution: Solution,
  version: SolutionVersion | null,
}>;

const OrganizationRowCell = ({
  tasks,
  isColumnExpanded,
}: OrganizationRowCellProps) => {
  const solutionVersionsById = useSolutionVersionsById();
  const solutions = useSolutions();
  const solutionsById = React.useMemo(() => {
    const map: Record<number, Solution> = {};

    solutions.forEach((solution) => {
      map[solution.id] = solution;
    });

    return map;
  }, [solutions]);

  const solutionsWithTasks = React.useMemo(() => {
    const tasksBySolution: SolutionTasksMap = {};

    tasks.forEach((task) => {
      if (!tasksBySolution[task.solutionId]) {
        tasksBySolution[task.solutionId] = {
          tasks: [],
          version: solutionVersionsById[task.solutionVersionId],
          solution: solutionsById[task.solutionId],
        };
      }

      tasksBySolution[task.solutionId].tasks.push(task);
    });

    return Object.values(tasksBySolution)
      .sort((a, b) => a.solution.name.localeCompare(b.solution.name));
  }, [tasks, solutionsById]);

  return (
    <div
      className={classNames(
        classes['cell'],
        isColumnExpanded && classes['column-expanded'],
      )}
      style={{
        width: isColumnExpanded
          ? EXPANDED_COLUMN_WIDTH
          : COLLAPSED_COLUMN_WIDTH,
      }}
    >
      <div className={`${classes['container']} space-y-6`}>
        {solutionsWithTasks.length === 0 && (
          <div
            className="text-xxs font-light uppercase text-center w-full pt-2 leading-tight"
          >
            Ingen<br />
            sager
          </div>
        )}
        {solutionsWithTasks.map(({ solution, tasks, version }) => (
          <div key={solution.id}>
            <div
              className={classes['solution-name']}
            >
              {solution.name}
              {version && (
                <span className="font-normal ml-1.5 opacity-75">v{version.name}</span>
              )}
            </div>
            <TaskList tasks={tasks} isExpanded={isColumnExpanded} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrganizationRowCell;
